import React, { useState, useEffect } from 'react';
import style from './qna.module.scss';
import { WithOverrides } from '../../components/WithOverrides';
import { DialogsList, Dialog, Content } from './components';
import { Content as TContent, Dialog as TDialog } from './types';
import { useQuery, useAppAndBranchId, useMutate, usePermissions } from '../../hooks';
import { api } from './api';
import { Modal } from '../../components';
import { ReactSortable } from 'react-sortablejs';
import { useSelector } from 'react-redux'
import NoData from "../../../../NoData";
import { ReactComponent as WarningIcon } from '../../../../../../assets/svg/warningIcon.svg'
import { useToast } from '../../../../../../helpers/commonFunction';
import { errorMessage } from '../../utils/error';
import { RootState } from '../../../../../../stateManager/store';

const sortableOptions = {
	animation: 150,
	fallbackOnBody: true,
	swapThreshold: 0.65,
	ghostClass: 'ghost',
	group: 'shared',
	disabled: false,
	handle: '.drag-handle',
};

export function SoloChatQNA() {
  const canView = usePermissions('dialog.View')
  const { ErrorToast } = useToast()
  const { selectedApp: {chatType} } = useSelector((state: RootState) => state.apps)
	const [content, setContent] = useState<{
		visibility: boolean;
		data: TContent | null;
	}>({
		visibility: false,
		data: null,
	});

	const [selectedDialog, setSelectedDialog] = useState<string>('');
	const [appId, branchId] = useAppAndBranchId();
	const { data: dialogGroups, refetch, loading: dialogsLoading } = useQuery(() =>
		api.getDialogs(appId, branchId), branchId
	);
	const { mutate: deleteMutate, loading: deleteLoading } = useMutate(
		api.deleteDialog,
	);
	const { mutate: editMutate } = useMutate(api.editDialog);
	const [dialogs, setDialogs] = useState<TDialog[]>([]);

	const [modal, setModal] = useState({
		dialogGroupId: '',
		visibility: false,
	});

	const deleteDialogName = dialogs.find(dialog => dialog.id === modal.dialogGroupId)?.title

  
  function openContent(data: TContent) {
    setContent({
      visibility: true,
      data,
    })
  }

	function closeContent() {
		setContent({
			visibility: false,
			data: null,
		});
	}

	async function deleteDialog(dialogGroupId: string) {
		setModal({
			visibility: true,
			dialogGroupId,
		});
	}

	useEffect(() => {
		if (selectedDialog && Array.isArray(dialogGroups)) {
		  const dialogs = dialogGroups.find((item) => {
		      const [value, type] = selectedDialog.split(':');
		      return item.dialogValue === value && item.dialogType === type;
		    })?.dialogGroups ?? [];
		  setDialogs(dialogs);
		}
	}, [selectedDialog, dialogGroups]);

  return (canView ?
    <WithOverrides>
      <div className={style.qna}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: `1fr ${content.visibility ? '383px' : ''}`,
          height: "100%",
        }}>
          <div style={{ overflow: "hidden", paddingRight: "0.25rem" }}>
            <Header chatType={chatType} />
            <div className={style.main}>
              <DialogsList openDialog={(dialog) => setSelectedDialog(dialog)} selectedDialog={selectedDialog} dialogGroups={dialogGroups ?? []} loading={dialogsLoading} dialogCount= {dialogs?.length || 0} />
              <ReactSortable className={style.dialogs} list={dialogs} setList={setDialogs} {...sortableOptions} onEnd={(data) => {
                const id = data.item.id.replace("qna-dialog-", "")
                if(dialogs.length > 1) {
                  editMutate(appId, id, {
                    branchId,
                    order: data.newIndex + 1,
                  }).then(()=> refetch())
                }
              }}>
                {dialogs.map(el => <Dialog key={el.title} dialog={el} openContent={openContent} deleteDialog={deleteDialog} />)}
              </ReactSortable>
            </div>
          </div>
          {content.data !== null && <Content close={closeContent} content={content.data} />}
        </div>
      </div>
      <Modal 
        styles={{
          '--ok-btn-bg': '#DE0000',
          '--ok-btn-filter': 'drop-shadow(0px 8px 13px rgba(211, 0, 73, 0.18))',
        }}
        okText="Delete"
        loading={deleteLoading}
        onOk={async () => {
          try {
            await deleteMutate(appId, branchId, modal.dialogGroupId)
            setModal({ visibility: false, dialogGroupId: "" })
            refetch()
          } catch(err) {
            ErrorToast(errorMessage("delete", "dialog", err.code))
          }
        }}
        onCancel={() => setModal({ visibility: false, dialogGroupId: "" })}
        isOpen={modal.visibility}
        title="Remove Dialog Group"
      >
        <div style={{
          padding: '18px 14px',
          color: '#393939',
          fontSize: 15,
          fontWeight: 500,
        }}>
          <p>Do you really want to remove the dialog group?</p>
          {deleteDialogName && (
            <span className="modal-delete-entity">{deleteDialogName}</span>
          )}
        </div>
      </Modal>
    </WithOverrides> :
     <div className="viewWrapper">
       <NoData
         svg={WarningIcon}
         header='Not Authorized'
         content='Please request owner of this organization to provide access'
         noButton={true}
       />
   </div>
  )
}

function Header({ chatType }: {chatType: string}) {
	return (
		<div className={style.header}>
			<h1>{chatType === 'genai' ? 'Events' : 'Dialogs'}</h1>
			<p className={style.desc}>Create or view the {chatType === 'genai' ? 'events' : 'dialogs'}</p>
			<hr style={{ borderTop: '1px solid rgba(149, 171, 211, 0.30)' }} />
		</div>
	);
}
